import { create } from "zustand";

type Updater<T> = T | ((previous: T) => T);

const resolveValue = <T>(value: Updater<T>, previous: T): T =>
  typeof value === "function" ? (value as (previous: T) => T)(previous) : value;

const TIME_ENTRY_HIGHLIGHT_DURATION_MS = 3000;
let highlightTimeEntryTimer: ReturnType<typeof setTimeout> | null = null;

type JsonData = {
  datas: any[];
  [key: string]: any;
};

type TimeEntriesState = {
  filter: number;
  weekData: any[];
  selectedWeekDay: any;
  statusFilter: any;
  jsonData: JsonData;
  refreshFlag: number;
  timeEntriesCount: number | null;
  step: number;
  weekLoggedHours: number;
  codes: any;
  minMinutes: any;
  minDailyWorkingHours: any;
  featureAiUsage: any;
  selectedMatters: any[];
  highlightedTimeEntryId: string | number | null;
  setFilter: (filter: Updater<number>) => void;
  setWeekData: (weekData: any[]) => void;
  setSelectedWeekDay: (selectedWeekDay: any) => void;
  setStatusFilter: (statusFilter: any) => void;
  setJsonData: (jsonData: Updater<JsonData>) => void;
  setRefreshFlag: (refreshFlag: Updater<number>) => void;
  setTimeEntriesCount: (timeEntriesCount: number | null) => void;
  setStep: (step: Updater<number>) => void;
  setWeekLoggedHours: (weekLoggedHours: number) => void;
  setCodes: (codes: any) => void;
  setMinMinutes: (minMinutes: any) => void;
  setMinDailyWorkingHours: (minDailyWorkingHours: any) => void;
  setFeatureAiUsage: (featureAiUsage: any) => void;
  setSelectedMatters: (selectedMatters: Updater<any[]>) => void;
  highlightTimeEntry: (id: string | number) => void;
};

export const useTimeEntriesStore = create<TimeEntriesState>((set) => ({
  filter: 3,
  weekData: [],
  selectedWeekDay: undefined,
  statusFilter: null,
  jsonData: { datas: [] },
  refreshFlag: 0,
  timeEntriesCount: null,
  step: 0,
  weekLoggedHours: 0,
  codes: null,
  minMinutes: false,
  minDailyWorkingHours: null,
  featureAiUsage: null,
  selectedMatters: [],
  highlightedTimeEntryId: null,
  setFilter: (filter) => set((state) => ({ filter: resolveValue(filter, state.filter) })),
  setWeekData: (weekData) => set({ weekData }),
  setSelectedWeekDay: (selectedWeekDay) => set({ selectedWeekDay }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setJsonData: (jsonData) => set((state) => ({ jsonData: resolveValue(jsonData, state.jsonData) })),
  setRefreshFlag: (refreshFlag) => set((state) => ({ refreshFlag: resolveValue(refreshFlag, state.refreshFlag) })),
  setTimeEntriesCount: (timeEntriesCount) => set({ timeEntriesCount }),
  setStep: (step) => set((state) => ({ step: resolveValue(step, state.step) })),
  setWeekLoggedHours: (weekLoggedHours) => set({ weekLoggedHours }),
  setCodes: (codes) => set({ codes }),
  setMinMinutes: (minMinutes) => set({ minMinutes }),
  setMinDailyWorkingHours: (minDailyWorkingHours) => set({ minDailyWorkingHours }),
  setFeatureAiUsage: (featureAiUsage) => set({ featureAiUsage }),
  setSelectedMatters: (selectedMatters) => set((state) => ({ selectedMatters: resolveValue(selectedMatters, state.selectedMatters) })),
  highlightTimeEntry: (id) => {
    if (highlightTimeEntryTimer) clearTimeout(highlightTimeEntryTimer);

    set({ highlightedTimeEntryId: id });
    highlightTimeEntryTimer = setTimeout(() => {
      set({ highlightedTimeEntryId: null });
      highlightTimeEntryTimer = null;
    }, TIME_ENTRY_HIGHLIGHT_DURATION_MS);
  },
}));
